import { useReveal } from "@/hooks/useReveal";
import { Link } from "react-router-dom";

const processSteps = [
  {
    step: "01",
    title: "Discovery & Analysis",
    body: "We dive deep into your business goals, user needs, and technical requirements. Every project starts with thorough research and strategic planning.",
  },
  {
    step: "02",
    title: "Design & Architecture",
    body: "Our UI/UX designers and solution architects craft intuitive interfaces and robust system architecture tailored to your specific use case.",
  },
  {
    step: "03",
    title: "Development & Testing",
    body: "Experienced developers bring designs to life with clean, scalable code. Rigorous QA ensures everything works flawlessly before launch.",
  },
  {
    step: "04",
    title: "Launch & Growth",
    body: "We deploy your solution and provide ongoing support. Our marketing team helps you reach your audience and scale your product effectively.",
  },
];

const stats = [
  { label: "Projects delivered", value: "150+" },
  { label: "Client retention", value: "92%" },
  { label: "Team members", value: "50+" },
  { label: "Countries served", value: "12" },
];

export default function StudioStrip() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <section ref={ref} className="bg-sand py-24 md:py-32">
      <div className="mx-auto max-w-[1320px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-5" data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-ink/60">How we work</span>
            <h2 className="mt-4 text-display text-4xl md:text-6xl leading-[1.05] text-balance">
              From idea to <span className="italic font-light">impact.</span>
            </h2>
            <p className="mt-6 text-ink/70 text-base md:text-lg leading-relaxed">
              We accompany projects at every stage — from idea and analytics to development,
              launch, and ongoing support. Speed, transparency, and effectiveness are our core principles.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink text-cream px-7 py-4 text-xs font-semibold uppercase tracking-widest2 cursor-pointer whitespace-nowrap hover:bg-terracotta transition-colors"
            >
              Start your project
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </Link>
          </div>

          <div className="lg:col-span-7" data-reveal data-reveal-delay="100">
            <div className="space-y-4">
              {processSteps.map((s) => (
                <div
                  key={s.step}
                  className="group rounded-2xl bg-cream p-6 md:p-8 border border-ink/5 hover:border-ink/15 transition-all duration-300 cursor-default"
                >
                  <div className="flex items-start gap-5">
                    <span className="text-display text-3xl text-ink/20 group-hover:text-lime-accent transition-colors shrink-0">
                      {s.step}
                    </span>
                    <div>
                      <h3 className="text-display text-xl md:text-2xl mb-2">{s.title}</h3>
                      <p className="text-ink/60 text-sm md:text-base leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 rounded-2xl overflow-hidden" data-reveal data-reveal-delay="200">
          {stats.map((s) => (
            <div key={s.label} className="bg-sand px-6 py-8 text-center">
              <div className="text-display text-3xl md:text-4xl text-ink">{s.value}</div>
              <div className="text-[10px] uppercase tracking-widest2 text-ink/50 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}